/** @format */

import { getAssetBlockKey, getQuestionContentBlocks, type Question } from "@question-bank/shared";
import { getAssetUrl } from "../../api/questions";
import { SVG_DISPLAY_WIDTH } from "./layout";

const SVG_RASTER_SCALE = 2;

const MAX_CANVAS_DIMENSION = 4096;

const DEFAULT_SVG_WIDTH = 300;
const DEFAULT_SVG_HEIGHT = 150;

export interface PrefetchedImage {
	dataUri: string;
	width: number | null;
	height: number | null;
	isVector: boolean;
}

const warnImageFailure = (url: string, reason: unknown): void => {
	console.warn(`PDF export: failed to prefetch image "${url}"`, reason);
};

const isSvgImage = (blob: Blob, url: string): boolean => {
	if (blob.type === "image/svg+xml") {
		return true;
	}
	const hasGenericType = blob.type === "" || blob.type === "application/octet-stream";
	return hasGenericType && /\.svg($|[?#])/i.test(url);
};

const svgToDataUrl = (svgText: string): string => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;

const loadImage = (src: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error("image failed to load"));
		image.src = src;
	});

const parseViewBox = (svgText: string): { width: number; height: number } | null => {
	const match = svgText.match(/viewBox\s*=\s*["']([^"']+)["']/i);
	if (!match) {
		return null;
	}
	const parts = match[1]
		.trim()
		.split(/[\s,]+/)
		.map(Number);
	if (parts.length !== 4 || parts.some(Number.isNaN)) {
		return null;
	}
	return parts[2] > 0 && parts[3] > 0 ? { width: parts[2], height: parts[3] } : null;
};

const resolveSvgDimensions = (image: HTMLImageElement, svgText: string): { width: number; height: number } => {
	if (image.naturalWidth > 0 && image.naturalHeight > 0) {
		return { width: image.naturalWidth, height: image.naturalHeight };
	}
	return parseViewBox(svgText) ?? { width: DEFAULT_SVG_WIDTH, height: DEFAULT_SVG_HEIGHT };
};

const sizeSvgMarkup = (svgText: string, width: number, height: number): string => {
	const root = new DOMParser().parseFromString(svgText, "image/svg+xml").documentElement;
	root.setAttribute("width", String(width));
	root.setAttribute("height", String(height));
	return new XMLSerializer().serializeToString(root);
};

const rasteriseSvgBlob = async (blob: Blob, url: string): Promise<PrefetchedImage | null> => {
	try {
		const svgText = await blob.text();
		const measured = await loadImage(svgToDataUrl(svgText));
		const { width, height } = resolveSvgDimensions(measured, svgText);
		const scale = Math.min(
			(SVG_DISPLAY_WIDTH * SVG_RASTER_SCALE) / width,
			MAX_CANVAS_DIMENSION / width,
			MAX_CANVAS_DIMENSION / height,
		);
		const canvasWidth = Math.max(1, Math.ceil(width * scale));
		const canvasHeight = Math.max(1, Math.ceil(height * scale));
		const sized = await loadImage(svgToDataUrl(sizeSvgMarkup(svgText, canvasWidth, canvasHeight)));
		const canvas = document.createElement("canvas");
		canvas.width = canvasWidth;
		canvas.height = canvasHeight;
		const context = canvas.getContext("2d");
		if (!context) {
			throw new Error("canvas 2D context unavailable");
		}
		context.drawImage(sized, 0, 0, canvasWidth, canvasHeight);
		const dataUrl = canvas.toDataURL("image/png");
		canvas.width = 0;
		canvas.height = 0;
		return { dataUri: dataUrl, width, height, isVector: true };
	} catch (error) {
		warnImageFailure(url, error);
		return null;
	}
};

const blobToDataUri = (blob: Blob, url: string): Promise<string | null> =>
	new Promise((resolve) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			if (typeof reader.result === "string") {
				resolve(reader.result);
			} else {
				warnImageFailure(url, reader.error ?? new Error("FileReader produced no result"));
				resolve(null);
			}
		};
		reader.readAsDataURL(blob);
	});

const measureBitmap = async (dataUri: string, url: string): Promise<PrefetchedImage> => {
	try {
		const image = await loadImage(dataUri);
		return { dataUri, width: image.naturalWidth, height: image.naturalHeight, isVector: false };
	} catch (error) {
		warnImageFailure(url, error);
		return { dataUri, width: null, height: null, isVector: false };
	}
};

/** Fetch an image URL and return it as a base64 data URI (or `null` on failure). */
export const fetchImageAsDataUri = async (url: string | null): Promise<PrefetchedImage | null> => {
	if (!url) {
		return null;
	}
	try {
		const response = await fetch(url, { headers: { Accept: "*/*" } });
		if (!response.ok) {
			warnImageFailure(url, `HTTP ${response.status}`);
			return null;
		}
		const blob = await response.blob();
		if (isSvgImage(blob, url)) {
			return await rasteriseSvgBlob(blob, url);
		}
		const dataUri = await blobToDataUri(blob, url);
		return dataUri === null ? null : await measureBitmap(dataUri, url);
	} catch (error) {
		warnImageFailure(url, error);
		return null;
	}
};

/**
 * Prefetch every image referenced by the questions' content asset blocks (for
 * the given language) into base64 data URIs, keyed by asset key. `@react-pdf`
 * cannot fetch images at render time, so the export must resolve them upfront.
 */
export const fetchContentImageMap = async (
	questions: Question[],
	language: "en" | "cy",
): Promise<Map<string, PrefetchedImage | null>> => {
	const entries: { key: string; url: string }[] = [];
	const seen = new Set<string>();
	for (const question of questions) {
		for (const block of getQuestionContentBlocks(question)) {
			if (block.type !== "asset") {
				continue;
			}
			const key = getAssetBlockKey(block, language);
			if (key && !seen.has(key)) {
				seen.add(key);
				entries.push({ key, url: getAssetUrl(question.brand, key) });
			}
		}
	}
	const images = await Promise.all(entries.map((entry) => fetchImageAsDataUri(entry.url)));
	const map = new Map<string, PrefetchedImage | null>();
	entries.forEach((entry, index) => map.set(entry.key, images[index]));
	return map;
};
